import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { V as VisibilityManager } from './index2.js';
import { D as DomService } from './index3.js';
import { r as remCalc } from './index5.js';
import { d as defineCustomElement$1 } from './roche-icon2.js';
import { c as isArrayLike, b as isIndex, e as eq } from './isArrayLike.js';
import { a as toNumber, i as isObject } from './throttle.js';

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0,
    MAX_INTEGER = 1.7976931348623157e+308;

/**
 * Converts `value` to a finite number.
 *
 * @static
 * @memberOf _
 * @since 4.12.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {number} Returns the converted number.
 * @example
 *
 * _.toFinite(3.2);
 * // => 3.2
 *
 * _.toFinite(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toFinite(Infinity);
 * // => 1.7976931348623157e+308
 *
 * _.toFinite('3.2');
 * // => 3.2
 */
function toFinite(value) {
  if (!value) {
    return value === 0 ? value : 0;
  }
  value = toNumber(value);
  if (value === INFINITY || value === -INFINITY) {
    var sign = (value < 0 ? -1 : 1);
    return sign * MAX_INTEGER;
  }
  return value === value ? value : 0;
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq(object[index], value);
  }
  return false;
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeCeil = Math.ceil,
    nativeMax = Math.max;

/**
 * The base implementation of `_.range` and `_.rangeRight` which doesn't
 * coerce arguments.
 *
 * @private
 * @param {number} start The start of the range.
 * @param {number} end The end of the range.
 * @param {number} step The value to increment or decrement by.
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Array} Returns the range of numbers.
 */
function baseRange(start, end, step, fromRight) {
  var index = -1,
      length = nativeMax(nativeCeil((end - start) / (step || 1)), 0),
      result = Array(length);

  while (length--) {
    result[fromRight ? length : ++index] = start;
    start += step;
  }
  return result;
}

/**
 * Creates a `_.range` or `_.rangeRight` function.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new range function.
 */
function createRange(fromRight) {
  return function(start, end, step) {
    if (step && typeof step != 'number' && isIterateeCall(start, end, step)) {
      end = step = undefined;
    }
    // Ensure the sign of `-0` is preserved.
    start = toFinite(start);
    if (end === undefined) {
      end = start;
      start = 0;
    } else {
      end = toFinite(end);
    }
    step = step === undefined ? (start < end ? 1 : -1) : toFinite(step);
    return baseRange(start, end, step, fromRight);
  };
}

/**
 * Creates an array of numbers (positive and/or negative) progressing from
 * `start` up to, but not including, `end`. A step of `-1` is used if a negative
 * `start` is specified without an `end` or `step`. If `end` is not specified,
 * it's set to `start` with `start` then set to `0`.
 *
 * **Note:** JavaScript follows the IEEE-754 standard for resolving
 * floating-point values which can produce unexpected results.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {number} [start=0] The start of the range.
 * @param {number} end The end of the range.
 * @param {number} [step=1] The value to increment or decrement by.
 * @returns {Array} Returns the range of numbers.
 * @see _.inRange, _.rangeRight
 * @example
 *
 * _.range(4);
 * // => [0, 1, 2, 3]
 *
 * _.range(-4);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 5);
 * // => [1, 2, 3, 4]
 *
 * _.range(0, 20, 5);
 * // => [0, 5, 10, 15]
 *
 * _.range(0, -4, -1);
 * // => [0, -1, -2, -3]
 *
 * _.range(1, 4, 0);
 * // => [1, 1, 1]
 *
 * _.range(0);
 * // => []
 */
var range = createRange();

const range$1 = range;

const rochePaginationCss = ":host{display:block;margin-top:3rem;display:block;overflow-x:auto}@media (min-width: 90rem){:host{margin-top:4.5rem}}:host,:host *{box-sizing:border-box}:host .pagination-row{display:flex;margin:0 auto;width:min-content}:host .pagination-row .large-numbers{display:none}:host .pagination-row .pagination-item{height:2.5rem;width:2.5rem;text-align:center}:host .pagination-row .pagination-item:not(:last-child){margin:0 1rem 0 0}:host .pagination-row .pagination-item:nth-last-child(2){width:auto;white-space:nowrap}:host .pagination-row .pagination-item-text{font-family:\"roche-sans\";font-style:normal;font-weight:200;color:var(--color-text--primary);font-size:var(--body--font-size--small-up);line-height:1.8125rem;font-weight:400;line-height:2.5rem !important;color:#544f4f;letter-spacing:-1px;user-select:none}@media (min-width: 64rem){:host .pagination-row .pagination-item-text{font-size:var(--body--font-size--xlarge-up);line-height:2rem}}:host .pagination-row .pagination-item roche-icon{margin-top:0.5rem}:host .pagination-row .pagination-item--colored{fill:#544f4f}:host .pagination-row .pagination-item--colored:focus,:host .pagination-row .pagination-item--colored:focus-visible{outline:var(--color-interactive--focus) dashed 2px;outline-offset:unset}:host .pagination-row .pagination-item--colored:focus:not(:focus-visible){outline:none}:host .pagination-row .pagination-item--colored--active{background-color:var(--color-interactive--primary-hover);fill:#fff;cursor:default}:host .pagination-row .pagination-item--colored--active .pagination-item-text{color:#fff}:host .pagination-row .pagination-item--colored--disabled{background-color:transparent;cursor:default;fill:#c2bab5}:host .pagination-row .pagination-item--colored--disabled .pagination-item-text{color:#c2bab5}:host .pagination-row .pagination-item--colored:hover:not(:host .pagination-row .pagination-item--colored--disabled){background-color:#706b69;fill:#fff;cursor:pointer}:host .pagination-row .pagination-item--colored:hover:not(:host .pagination-row .pagination-item--colored--disabled) .pagination-item-text{color:#fff}:host .pagination-row .separator::after{display:inline;content:\"/\"}@media (min-width: 48rem){:host .pagination-row .large-numbers{display:block}:host .pagination-row .small-numbers{display:none}}:host([dir=rtl]) .pagination-item:not(:last-child){margin:0 0 0 1rem}:host([dir=rtl]) .separator::after{content:\"\\\\\"}";
const RochePaginationStyle0 = rochePaginationCss;

const RochePagination = /*@__PURE__*/ proxyCustomElement(class RochePagination extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.pageChangeEvent = createEvent(this, "rochePaginationPageChange", 7);
        // After which page should truncation start
        this.truncateStartPage = 5;
        // How many siblings should be rendered around the currently active page
        this.activePageRenderSpread = 1;
        /*
         * The Amount of Pages that will be shown without dots at the Left side,
         * if the selected page is smaller.
         */
        this.pagesWithoutPreDots = 5;
        /*
         * The Amount of Pages that will be shown without dots at the Right side,
         * if the selected page is smaller.
         */
        this.pagesWithoutAfterDots = 4;
        this.totalPages = undefined;
        this.activePage = 1;
        this.yOffsetTargetsTags = 'roche-header';
        this.disableScrollParentIntoView = false;
        this.previousPage = this.previousPage.bind(this);
        this.nextPage = this.nextPage.bind(this);
    }
    componentDidLoad() {
        DomService.handleTextDirection(this.element);
    }
    isInThreshold() {
        return this.totalPages <= this.truncateStartPage;
    }
    render() {
        return (h(Host, { key: 'b52ebcddec73125c46dfd19f0f740cbfed3b9abb' }, h("div", { key: '7e6bad58873511ee21fd47ac8c667e3f166d7e0e', class: "pagination-row" }, RochePagination.getArrowElement(this.activePage === 1, this.previousPage, 'chevron-left'), range$1(this.totalPages).flatMap((index) => [
            this.getNumberElement(index + 1),
            this.shouldShowPreDots(index) ? this.getDots() : null,
            this.shouldShowAfterDots(index) ? this.getDots() : null,
        ]), h("div", { key: '98532f282c7dec61b204c4ae26000975a3a85724', class: "pagination-item small-numbers" }, h("span", { key: '7cdf1621fb536a6074fc908d80fc64e4d2835ddd', class: "pagination-item-text" }, this.activePage, "\u2009", h("span", { key: 'f72d6802866b7a891e7c28de405c3fe5c2d3e820', class: "separator" }), "\u2009", this.totalPages)), RochePagination.getArrowElement(this.activePage === this.totalPages, this.nextPage, 'chevron-right'))));
    }
    static getArrowElement(disableCondition, onClick, icon) {
        return (h("div", { class: {
                'pagination-item--colored--disabled': disableCondition,
                'pagination-item': true,
                'pagination-item--colored': true,
                'pagination-arrow': true,
            }, onClick: onClick, tabIndex: 0 }, h("roche-icon", { icon: icon, width: `${remCalc(24)}rem`, height: `${remCalc(24)}rem` })));
    }
    getNumberElement(number) {
        const numberClass = {
            'large-numbers': true,
            'pagination-item': true,
            'pagination-number': true,
            'pagination-item--colored': true,
            'pagination-item--colored--active': this.activePage === number,
        };
        return (this.shouldShowNumber(number)
            ? h("div", { key: number, class: numberClass, onClick: () => this.setPage(number), tabIndex: 0 }, h("span", { class: "pagination-item-text" }, number))
            : false);
    }
    // eslint-disable-next-line class-methods-use-this
    getDots() {
        return (h("div", { class: {
                'large-numbers': true,
                'pagination-item--colored--disabled': true,
                'pagination-item': true,
            }, tabIndex: 0 }, h("span", { class: "pagination-item-text" }, "...")));
    }
    shouldShowPreDots(index) {
        return !this.isInThreshold()
            && index === 0
            && this.activePage >= this.pagesWithoutPreDots;
    }
    shouldShowAfterDots(index) {
        return !this.isInThreshold()
            && index === this.totalPages - 2
            && this.activePage <= this.totalPages - this.pagesWithoutAfterDots;
    }
    shouldShowNumber(number) {
        return this.isInThreshold()
            || this.isFirstOrLastPage(number)
            || this.isBesideSelectedPage(number)
            || this.shouldShowPreDotPages(number)
            || this.shouldShowAfterDotPages(number);
    }
    shouldShowPreDotPages(page) {
        return (this.activePage < this.pagesWithoutPreDots && page <= this.pagesWithoutPreDots);
    }
    shouldShowAfterDotPages(page) {
        return (this.activePage > this.totalPages - this.pagesWithoutAfterDots
            && page >= this.totalPages - this.pagesWithoutAfterDots);
    }
    isFirstOrLastPage(page) {
        return page === 1 || page === this.totalPages;
    }
    isBesideSelectedPage(page) {
        return (page >= this.activePage - this.activePageRenderSpread
            && page <= this.activePage + this.activePageRenderSpread);
    }
    previousPage() {
        if (this.activePage > 1) {
            this.setPage(this.activePage - 1);
        }
    }
    nextPage() {
        if (this.activePage < this.totalPages) {
            this.setPage(this.activePage + 1);
        }
    }
    setPage(page) {
        if (this.activePage === page) {
            return;
        }
        this.activePage = page;
        this.pageChangeEvent.emit(page);
        if (this.disableScrollParentIntoView) {
            return;
        }
        const scrollTarget = (DomService.closestElement('roche-grid', this.element.parentElement));
        if (!scrollTarget) {
            return;
        }
        VisibilityManager.scrollTo(scrollTarget);
    }
    get element() { return this; }
    static get style() { return RochePaginationStyle0; }
}, [1, "roche-pagination", {
        "totalPages": [2, "total-pages"],
        "activePage": [1538, "active-page"],
        "yOffsetTargetsTags": [1, "y-offset-targets-tags"],
        "disableScrollParentIntoView": [4, "disable-scroll-parent-into-view"]
    }]);
function defineCustomElement() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["roche-pagination", "roche-icon"];
    components.forEach(tagName => { switch (tagName) {
        case "roche-pagination":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, RochePagination);
            }
            break;
        case "roche-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$1();
            }
            break;
    } });
}

export { RochePagination as R, defineCustomElement as d };

//# sourceMappingURL=roche-pagination2.js.map